import { RefObject, useEffect, useRef } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { prefersReducedMotion } from "./gsap";

/**
 * Lenis on a scrolling element: the wheel glides instead of jumping in
 * steps. Touch keeps the native scroll, and so does anything that scrolls
 * by itself inside (a DataTable, the sidebar list) — Lenis passes the wheel
 * to it. Nothing is created when the system asks for less motion.
 *
 * Returns a ref to the instance, empty while smoothing is off.
 */
const useSmoothScroll = (
  wrapperRef: RefObject<HTMLElement>,
  enabled: boolean,
) => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!enabled || !wrapper || prefersReducedMotion()) return undefined;

    const lenis = new Lenis({
      wrapper,
      content: wrapper,
      eventsTarget: wrapper,
      autoRaf: true,
      lerp: 0.12,
      // Rows come in pages as the table scrolls: read the height each frame
      // rather than waiting for a resize to notice them.
      naiveDimensions: true,
      allowNestedScroll: true,
    });
    lenisRef.current = lenis;

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [wrapperRef, enabled]);

  return lenisRef;
};

export default useSmoothScroll;
